import { useQuery } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import ls from 'local-storage';
import s from 'underscore.string';

import { deaccentizeandlowerize } from '@libs/NwUtils';

function capitalize(s) {
    if (!s) return '';
    return s[0].toUpperCase() + s.slice(1).toLowerCase();
}

const modelFullName = (model, initialOnly, asArray) => {
    //initials of surname
    if (initialOnly) {
        if (model && model.Name) {
            return model.Surname
                ? `${capitalize(model.Name)} ${s.truncate(model.Surname, 1, '.').toUpperCase()}`
                : capitalize(model.Name);
        }
        if (model && model.N) {
            return model.S
                ? `${capitalize(model.N)} ${s.truncate(model.S, 1, '.').toUpperCase()}`
                : capitalize(model.N);
        }
    }
    //array [name, surname]
    if (asArray) {
        return [capitalize(model.N), model.S ? capitalize(model.S) : ''];
    }
    //full name
    if (model && model.Name) {
        return model.Surname ? `${capitalize(model.Name)} ${capitalize(model.Surname)}` : capitalize(model.Name);
    }
    if (model && model.N) {
        return model.S ? `${capitalize(model.N)} ${capitalize(model.S)}` : capitalize(model.N);
    }
    return '';
};

const fetchModels = async () => {
    const models = await Axios.get('/models');
    // Verifica che models sia un array
    if (!Array.isArray(models)) {
        console.warn('Models response is not an array:', models);
        return models;
    }
    // Pre-calcola le normalizzazioni per ottimizzare le ricerche
    const modelsWithNormalizedNames = models.map((model) => {
        const normalizedName = model.N ? deaccentizeandlowerize(model.N).trim() : '';
        const normalizedSurname = model.S ? deaccentizeandlowerize(model.S).trim() : '';
        const normalizedFullName = normalizedName + (normalizedSurname ? ' ' + normalizedSurname : '');
        const normalizedTagNames = model.T ? model.T.map((tag) => tag.Name.toLowerCase()) : [];

        return {
            ...model,
            _normalizedName: normalizedName,
            _normalizedSurname: normalizedSurname,
            _normalizedFullName: normalizedFullName,
            _normalizedTagNames: normalizedTagNames,
        };
    });
    return modelsWithNormalizedNames;
};

const staleTime = 300000;

const fetchModelsByArea = async () => {
    //area id not used anymore, not reliable. fetchModels is checking area on LS
    queryClient.invalidateQueries({ queryKey: ['models'] });
    await prefetchModels();
};

const prefetchModels = async () => {
    await queryClient.prefetchQuery({
        queryKey: ['models'],
        queryFn: fetchModels,
        staleTime: staleTime,
        cacheTime: Infinity,
        retry: false,
    });
};

const useModelsList = () => {
    return useQuery({
        queryKey: ['models'],
        queryFn: fetchModels,
        staleTime: staleTime,
        cacheTime: Infinity,
        retry: false,
    });
};

const fetchModelsManagement = async () => {
    const models = await Axios.post('/models/management', {
        StorageBookingFilter: 'All',
    });

    const modelsWithNormalizedNames = models.map((model) => {
        const normalizedName = model.N ? deaccentizeandlowerize(model.N).trim() : '';
        const normalizedSurname = model.S ? deaccentizeandlowerize(model.S).trim() : '';
        const normalizedFullName = normalizedName + (normalizedSurname ? ' ' + normalizedSurname : '');
        const normalizedTagNames = model.T ? model.T.map((tag) => tag.Name.toLowerCase()) : [];

        return {
            ...model,
            _normalizedName: normalizedName,
            _normalizedSurname: normalizedSurname,
            _normalizedFullName: normalizedFullName,
            _normalizedTagNames: normalizedTagNames,
        };
    });

    return modelsWithNormalizedNames;
};

const useModelsManagementList = () => {
    return useQuery({
        queryKey: ['models-management'],
        queryFn: fetchModelsManagement,
        staleTime: staleTime,
        cacheTime: Infinity,
        retry: false,
    });
};

const getModel = (ID) => {
    const models = queryClient.getQueryData(['models']);
    if (models) {
        const c = models.find((m) => m.ID === ID);
        if (c) {
            return c;
        }
        const contacts = getSimpleContacts();
        const contact = contacts.find((contact) => contact.ID === ID);
        if (contact) {
            return {
                ...contact,
                archived: true,
            };
        }
    }
    return null;
};

const validModelInContext = (modelId) => {
    const models = queryClient.getQueryData(['models']);
    if (models) {
        const c = models.find((m) => m.ID === Number(modelId));
        if (c) {
            return true;
        } else {
            return false;
        }
    }
    return true;
};

const validCustomerInContext = (customerId) => {
    const customers = queryClient.getQueryData(['customers']);
    if (customers) {
        const c = customers.find((cust) => cust.ID === Number(customerId));
        if (c) {
            return true;
        } else {
            return false;
        }
    }
    return true;
};

const getModels = (ids) => {
    const models = queryClient.getQueryData(['models']) ? queryClient.getQueryData(['models']) : [];
    if (ids && ids.length) {
        return models.filter((cust) => ids.includes(cust.ID));
    }
    models.sort((a, b) => (a.N + a.S).localeCompare(b.N + b.S));
    return models.map((model) => ({
        ...model,
        N: model.N ? capitalize(model.N) : '',
        S: model.S ? capitalize(model.S) : '',
    }));
};

const forceReloadModels = async () => {
    await queryClient.invalidateQueries({ queryKey: ['models'] });
};

/**
 * Aggiorna le main features di un modello nella cache.
 * Unico punto in cui `.FT` viene riscritto.
 */
const updateModelFeatures = (ID, mainFeatures) => {
    queryClient.setQueryData(['models'], (models) => {
        if (!Array.isArray(models)) {
            return models;
        }
        return models.map((model) => (model.ID === ID ? { ...model, FT: mainFeatures } : model));
    });
};

// networkAgencyId null aggiorna lo storage di tutte le network agency collegate alla modella
const updateModelsManagementStorage = (contactId, networkAgencyId, storageBooking) => {
    queryClient.setQueryData(['models-management'], (models) => {
        if (!Array.isArray(models)) {
            return models;
        }
        const modelId = Number(contactId);
        return models.map((model) => {
            if (Number(model.ID) !== modelId || !Array.isArray(model.NWAGS)) {
                return model;
            }
            return {
                ...model,
                NWAGS: model.NWAGS.map((networkAgency) => {
                    if (networkAgencyId != null && networkAgency.NetworkAgencyID !== Number(networkAgencyId)) {
                        return networkAgency;
                    }
                    return {
                        ...networkAgency,
                        StorageBooking: storageBooking,
                    };
                }),
            };
        });
    });
};

const reloadModelsManagement = () => {
    return queryClient.invalidateQueries({ queryKey: ['models-management'] });
};

const fetchPlaylistsCount = () => {
    return Axios.get('/models/PlaylistsCount');
};

const prefetchPlaylistsCount = () => {
    queryClient.prefetchQuery(['playlistsCount'], () => fetchPlaylistsCount(), {
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

/** Dopo aver cambiato fast package, compcard o favourite di un book gli array FP/CP/FAV sono vecchi. */
const forceReloadPlaylistsCount = () => {
    queryClient.invalidateQueries({ queryKey: ['playlistsCount'] });
};

const usePlaylistsCount = () => {
    return useQuery({
        queryKey: ['playlistsCount'],
        queryFn: fetchPlaylistsCount,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

//CUSTOMERS

const fetchCustomers = async () => {
    const area = getArea();
    let customers;

    if (area === 'accounting') {
        customers = await Axios.get('/customers/accounting');
    } else if (area === 'management') {
        customers = await Axios.post('/customers/management', {
            StorageBookingFilter: 'All',
        });
    } else {
        customers = await Axios.get('/customers');
    }

    return customers.map((customer) => {
        const normalizedName = customer.N ? deaccentizeandlowerize(customer.N).trim() : '';
        const normalizedTagNames = customer.T ? customer.T.map((tag) => tag.Name.toLowerCase()) : [];
        return {
            ...customer,
            N: customer.N ? customer.N.trim() : '',
            _normalizedName: normalizedName,
            _normalizedFullName: normalizedName,
            _normalizedTagNames: normalizedTagNames,
        };
    });
};

const fetchCustomersByArea = async () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    await prefetchCustomers();
};

const prefetchCustomers = () => {
    queryClient.prefetchQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const useCustomersList = () => {
    return useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const getCustomer = (ID) => {
    const customers = queryClient.getQueryData(['customers']);

    if (customers) {
        const c = customers ? customers.find((m) => m.ID === ID) : [];
        if (c) {
            return c;
        }
    }
    return null;
};

const getCustomers = (ids) => {
    const customers = queryClient.getQueryData(['customers']) ? queryClient.getQueryData(['customers']) : [];
    if (ids && ids.length) {
        return customers ? customers.filter((cust) => ids.includes(cust.ID)) : [];
    }
    return customers;
};

const forceReloadCustomers = async () => {
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
};

const customerFullName = (customer, initialOnly, asArray) => {
    //initials of surname
    if (initialOnly) {
        if (customer && customer.Name) {
            return customer.Surname && !customer.isCompany
                ? `${capitalize(customer.Name)} ${s.truncate(customer.Surname, 1, '.').toUpperCase()}`
                : capitalize(customer.Name);
        }
        if (customer && customer.N) {
            return customer.S && !customer.isCompany
                ? `${capitalize(customer.N)} ${s.truncate(customer.S, 1, '.').toUpperCase()}`
                : capitalize(customer.N);
        }
    }
    //array [name, surname]
    if (asArray) {
        return [capitalize(customer.N), customer.S ? capitalize(customer.S) : ''];
    }
    //full name
    if (customer && customer.Name) {
        return customer.Surname && !customer.isCompany
            ? `${capitalize(customer.Name)} ${capitalize(customer.Surname)}`
            : capitalize(customer.Name);
    }
    if (customer && customer.N) {
        return customer.S && !customer.isCompany
            ? `${capitalize(customer.N)} ${capitalize(customer.S)}`
            : capitalize(customer.N);
    }
    return '';
};

//CONTACTS - GENERIC

const fetchContacts = async (_) => {
    const contacts = await Axios.get('/contacts/accounting');
    return contacts.map((contact) => {
        const normalizedName = contact.N ? deaccentizeandlowerize(contact.N).trim() : '';
        const normalizedSurname = contact.S ? deaccentizeandlowerize(contact.S).trim() : '';
        const normalizedFullName = normalizedName + (normalizedSurname ? ' ' + normalizedSurname : '');
        //const normalizedTagNames = contact.T ? contact.T.map((tag) => tag.Name.toLowerCase()) : [];
        return {
            ...contact,
            N: contact.N ? contact.N.trim() : '',
            S: contact.S ? contact.S.trim() : null,
            _normalizedName: normalizedName,
            _normalizedSurname: normalizedSurname,
            _normalizedFullName: normalizedFullName,
            //_normalizedTagNames: normalizedTagNames,
        };
    });
};

const forceReloadContacts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['contacts'] });
};

const useContacts = () => {
    return useQuery({
        queryKey: ['contacts'],
        queryFn: fetchContacts,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const prefetchContacts = async () => {
    await queryClient.prefetchQuery({
        queryKey: ['contacts'],
        queryFn: fetchContacts,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const getAllContacts = () => {
    const customers = queryClient.getQueryData(['customers']);
    const models = queryClient.getQueryData(['models']);
    return customers && models ? [...models, ...customers] : [];
};

const getContact = (ID) => {
    const contacts = getAllContacts();
    if (contacts) {
        const c = contacts.find((m) => m.ID === ID);
        if (c) {
            return c;
        }
    }
    return null;
};

const isContactApproved = (ID) => {
    const contact = getContact(ID);
    return contact && contact.A;
};

const getContacts = (ids) => {
    const contacts = getAllContacts();
    if (ids && ids.length) {
        return contacts.filter((cont) => ids.includes(cont.ID));
    }
    return contacts;
};

const getSimpleContacts = () => {
    const contacts = queryClient.getQueryData(['contacts']);
    return contacts ? contacts : [];
};

const getSimpleContact = (ID) => {
    if (!ID) return '';
    const contactID = typeof ID === 'number' ? ID : parseInt(ID);
    const contacts = getSimpleContacts();
    const contact = (contacts || []).find((contact) => contact.ID === contactID);
    return contact;
};

const getContactName = (ID, asArray) => {
    const contact = getSimpleContact(ID);
    if (contact) {
        return contact.st > 3 ? modelFullName(contact, false, asArray) : customerFullName(contact, false, asArray);
    } else {
        return '';
    }
};

const contactsTypeMappingExact = {
    1: 'Client',
    2: 'Agency',
    3: 'Service',
    4: 'Model',
    5: 'ScoutingModel',
};

const contactsTypeMapping = {
    1: 'Client',
    2: 'Agency',
    3: 'Service',
    4: 'Model',
    5: 'Model',
};

const getContactType = (ID) => {
    // const contact = getContact(ID);
    const contact = getSimpleContact(ID);
    if (contact) {
        if (contact.ST in contactsTypeMapping) {
            return {
                type: contact.ST,
                name: contactsTypeMapping[contact.ST],
                exactType: contactsTypeMappingExact[contact.ST],
            };
        }
        return null;
    }
    return null;
};

//ACCOUNTING CONTACTS LIST

const fetchManagementContacts = async () => {
    const contacts = await Axios.get('/contacts/management');
    return contacts.map((contact) => {
        const normalizedName = contact.N ? deaccentizeandlowerize(contact.N).trim() : '';
        const normalizedSurname = contact.S ? deaccentizeandlowerize(contact.S).trim() : '';
        const normalizedFullName = normalizedName + (normalizedSurname ? ' ' + normalizedSurname : '');
        const normalizedTagNames = contact.T ? contact.T.map((tag) => tag.Name.toLowerCase()) : [];
        return {
            ...contact,
            N: contact.N ? contact.N.trim() : '',
            S: contact.S ? contact.S.trim() : null,
            _normalizedName: normalizedName,
            _normalizedSurname: normalizedSurname,
            _normalizedFullName: normalizedFullName,
            _normalizedTagNames: normalizedTagNames,
        };
    });
};

const useManagementContactsList = () => {
    return useQuery({
        queryKey: ['management-contacts'],
        queryFn: fetchManagementContacts,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const fetchForManagementArea = async () => {
    await queryClient.prefetchQuery({
        queryKey: ['contacts-management'],
        queryFn: () => Axios.get('/contacts/management'),
        staleTime: staleTime,
        cacheTime: Infinity,
    });
};

//UTILS
export const getArea = () => {
    const currentArea = ls.get('nw_config_area') ? ls.get('nw_config_area').area : '';
    return currentArea;
};

export {
    useModelsList,
    useModelsManagementList,
    forceReloadModels,
    getModel,
    getModels,
    useCustomersList,
    getCustomer,
    getCustomers,
    forceReloadCustomers,
    useContacts,
    getContact,
    isContactApproved,
    getContacts,
    getContactName,
    getContactType,
    prefetchModels,
    prefetchCustomers,
    prefetchContacts,
    modelFullName,
    customerFullName,
    forceReloadContacts,
    fetchModelsByArea,
    fetchCustomersByArea,
    fetchManagementContacts,
    useManagementContactsList,
    fetchForManagementArea,
    getSimpleContacts,
    fetchCustomers,
    usePlaylistsCount,
    prefetchPlaylistsCount,
    forceReloadPlaylistsCount,
    updateModelFeatures,
    updateModelsManagementStorage,
    reloadModelsManagement,
    validModelInContext,
    validCustomerInContext,
};
